/** The proposal panel's Resend wording (components/ProposalActions.tsx); pure, tested with node --test. */
import type { MessageKey } from "../i18n/keys";
import { intlLocale } from "./format.ts";
import type { Tr } from "./tr.ts";

type ResendState = {
  allowed: boolean;
  next_allowed_at?: string | null;
  blocked_by?: string | null;
  recipient?: string | null;
  last_status?: "scheduled" | "sent" | "failed" | null;
  last_requested_at?: string | null;
};

/** "today 14:05", "tomorrow 14:05", else "Mon, Oct 12, 14:05" (the dashboard's language, the viewer's time zone). */
export function whenText(iso: string, t: Tr, now: Date = new Date(), locale: string | undefined = intlLocale()): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day(at) - day(now)) / 86_400_000);
  if (days === 0) return t("designs.resend.today", { time });
  if (days === 1) return t("designs.resend.tomorrow", { time });
  return at.toLocaleString(locale, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** What the Resend button says and the line under the actions. */
export function resendView(state: ResendState | undefined, t: Tr, now: Date = new Date()): { label: string; busy: boolean; done: boolean; note: string | null } {
  if (!state) return { label: t("designs.resend.resend"), busy: false, done: false, note: null };
  const recent = state.last_requested_at && now.getTime() - new Date(state.last_requested_at).getTime() < 10 * 60_000;
  const busy = state.last_status === "scheduled";
  const done = !busy && state.last_status === "sent" && !!recent;
  const label = t(busy ? "designs.resend.scheduled" : done ? "designs.resend.sent" : "designs.resend.resend");
  let note: string | null = null;
  if (state.blocked_by === "no_email") note = t("designs.resend.noEmail");
  else if (state.last_status === "failed" && state.allowed) note = t("designs.resend.lastFailed");
  else if (!state.allowed && state.next_allowed_at) note = t("designs.resend.nextAt", { when: whenText(state.next_allowed_at, t, now) });
  const to = state.recipient;
  if (busy) note = to ? t("designs.resend.emailing", { recipient: to }) : t("designs.resend.emailingHomeowner");
  else if (done) note = [to ? t("designs.resend.sentTo", { recipient: to }) : t("designs.resend.sentToHomeowner"), note].filter(Boolean).join(" ");
  return { label, busy, done, note };
}

/** The lead's proposal after a design save (`ProposalState.status`). */
export type ProposalStatus = "ready" | "updating" | "failed";

export const UPDATING_LABEL: MessageKey = "designs.proposal.updating";
export const UPDATING_TOOLTIP: MessageKey = "designs.proposal.updatingTooltip";
export const FAILED_NOTE: MessageKey = "designs.proposal.updateFailed";

/** Whether the proposal actions (Resend, Download proposal) wait on a proposal update, and what they say. */
export function proposalView(status: ProposalStatus | null | undefined, t: Tr): { blocked: boolean; updating: boolean; failed: boolean; tooltip: string | null } {
  const updating = status === "updating";
  const failed = status === "failed";
  return {
    blocked: updating || failed,
    updating,
    failed,
    tooltip: updating ? t(UPDATING_TOOLTIP) : failed ? t("designs.proposal.updateFailedTooltip") : null,
  };
}

/** "1 a day and 2 a week for each quote" from the API's limits. */
export function limitsText(limits: { per_quote_day: number; per_quote_week: number } | null | undefined, t: Tr): string | null {
  if (!limits) return null;
  return t("designs.resend.limits", { day: limits.per_quote_day, week: limits.per_quote_week });
}

/** The Resend confirmation's wording. The email carries the quote and its permanent link (never the PDF). */
export function confirmCopy(input: { name?: string | null; email?: string | null; address?: string | null; quoteNumber?: string | null }, t: Tr): { title: string; body: string } {
  const name = input.name && input.name !== input.email ? input.name : null; // a lead without a name shows its email
  const recipient = name && input.email ? "both" : input.email || name ? "one" : "none";
  const body = t("designs.resend.confirmBody", {
    recipient,
    name: name ?? "",
    email: input.email ?? "",
    contact: input.email || name || "",
    hasNumber: input.quoteNumber ? "yes" : "no",
    quoteNumber: input.quoteNumber ?? "",
    hasAddress: input.address ? "yes" : "no",
    address: input.address ?? "",
  });
  return { title: t("designs.resend.confirmTitle"), body };
}
